<script setup lang="ts">
import type { TabMenuItem } from '~/constants/tab'
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { faBook, faXmark } from '@fortawesome/pro-light-svg-icons'

type SourceTab = 'literature-list'

const props = defineProps<{
  literatureList?: LawAndCommentary | null
  isLoading?: boolean
}>()

const defaultTab: TabMenuItem<SourceTab> = {
  value: 'literature-list',
  label: 'Litteraturliste',
  icon: faBook,
}

const tabOptions: TabMenuItem<SourceTab>[] = [defaultTab]
const selectedTab = ref<TabMenuItem<SourceTab>>(defaultTab)

const modalRef = useTemplateRef<{ show: () => void, hide: () => void }>('modalRef')

defineExpose({
  show() {
    selectedTab.value = defaultTab
    modalRef.value?.show?.()
  },
  hide() {
    modalRef.value?.hide?.()
  },
})
</script>

<template>
  <Modal ref="modalRef" class="law-sources-modal" drawer-position="right" drawer-size="var(--sources-modal-drawer-size)">
    <template #header>
      <div class="flex w-full items-start justify-between gap-m">
        <h4 class="font-bold text-base text-primary">
          Kilder til kommentaren
        </h4>
        <ControlButton
          size="small"
          :toggle-selected="false"
          :icon="faXmark"
          class="bg-transparent hover:bg-surface-hover border rounded-lg"
          aria-label="Lukk kilder"
          @click="modalRef?.hide?.()"
        />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-m h-full min-h-0">
        <Tabs v-model="selectedTab" :options="tabOptions" />

        <div v-if="isLoading" class="flex flex-col gap-m px-xs pb-xs">
          <Skeleton class="h-4 w-1/3" />
          <Skeleton class="h-4 w-full" />
          <Skeleton class="h-4 w-11/12" />
          <Skeleton class="h-4 w-10/12" />
        </div>

        <div v-else-if="selectedTab.value === 'literature-list' && literatureList" class="min-h-0 flex-1 overflow-y-auto scrollbar-carbon">
          <LawLiteratureList
            :comment="literatureList"
          />
        </div>

        <div v-else class="text-small text-secondary px-xs pb-xs">
          Denne loven har dessverre ingen litteraturliste ennå
        </div>
      </div>
    </template>

    <template #footer>
      <div />
    </template>
  </Modal>
</template>

<style lang="postcss">
.ks-dialog.law-sources-modal {
  --sources-modal-drawer-size: 100vw;

  @apply !h-dvh !max-h-dvh !p-8 overflow-hidden flex flex-col bg-surface-elevated;
  border-top-left-radius: 32px !important;
  border-bottom-left-radius: 32px !important;
}

@media (min-width: 1024px) {
  .ks-dialog.law-sources-modal {
    --sources-modal-drawer-size: min(40vw, 620px);
  }
}

@media (min-width: 1280px) {
  .ks-dialog.law-sources-modal {
    --sources-modal-drawer-size: min(38vw, 620px);
  }
}

.ks-dialog.law-sources-modal .ks-dialog-header {
  @apply !w-full !pt-0 !px-0 !pb-m;
}

.ks-dialog.law-sources-modal .ks-dialog-body {
  @apply !p-0 flex-1 min-h-0 overflow-y-auto overflow-x-hidden;
}

.ks-dialog.law-sources-modal .ks-dialog-footer {
  @apply !hidden;
}

</style>
